import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Badge, type BadgeTone } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { ConfirmDialog } from '../../../components/ui/Dialog';
import { EmptyState } from '../../../components/ui/EmptyState';
import { CheckboxField, SelectField, TextField } from '../../../components/ui/Field';
import {
  DataTable,
  ErrorState,
  PageContainer,
  PageHeader,
  Pagination,
  TableSkeleton,
} from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { useAuth, useCan } from '../../auth/AuthProvider';
import type { Scope } from '../../auth/types';
import { SCOPE_LABELS, teamApi, teamKeys, type StaffMember } from '../api';
import { InviteStaffDialog } from '../components/InviteStaffDialog';

const STATUS: Record<StaffMember['status'], { label: string; tone: BadgeTone }> = {
  active: { label: 'Active', tone: 'success' },
  invited: { label: 'Invited', tone: 'info' },
  disabled: { label: 'Deactivated', tone: 'neutral' },
};

export function StaffListPage() {
  const can = useCan();
  const navigate = useNavigate();
  const [inviting, setInviting] = useState(false);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const query = { page, pageSize: 25, ...(q ? { q } : {}), ...(status ? { status } : {}) };
  const staff = useQuery({
    queryKey: teamKeys.staff(query),
    queryFn: () => teamApi.staff(query),
    placeholderData: keepPreviousData,
  });

  return (
    <PageContainer>
      <PageHeader
        title="Staff"
        description="People who can sign in to your account."
        actions={
          can('staff.create') ? (
            <Button leadingIcon={<Plus aria-hidden className="size-4" />} onClick={() => setInviting(true)}>
              Invite staff
            </Button>
          ) : null
        }
      />
      <Card>
        <div className="flex flex-wrap gap-3 border-b border-line p-4">
          <input
            type="search"
            aria-label="Search staff"
            placeholder="Search by name or email"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="h-9 min-w-56 flex-1 rounded-md border border-line bg-surface px-3 text-sm"
          />
          <select
            aria-label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-md border border-line bg-surface px-2 text-sm"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="invited">Invited</option>
            <option value="disabled">Deactivated</option>
          </select>
        </div>
        {staff.isPending ? (
          <TableSkeleton />
        ) : staff.isError ? (
          <div className="p-4">
            <ErrorState error={staff.error} onRetry={() => void staff.refetch()} />
          </div>
        ) : (
          <>
            <DataTable
              rows={staff.data.data}
              rowKey={(s) => s.id}
              onRowClick={(s) => navigate(`/app/team/staff/${s.id}`)}
              empty={<EmptyState icon={<Users className="size-6" />} title="No staff found" />}
              columns={[
                {
                  header: 'Name',
                  cell: (s) => (
                    <div>
                      <p className="font-medium text-fg">{s.fullName}</p>
                      <p className="text-xs text-fg-subtle">{s.email}</p>
                    </div>
                  ),
                },
                { header: 'Role', cell: (s) => s.roles.map((r) => r.name).join(', ') || '—' },
                { header: 'Branch', cell: (s) => s.primaryBranch?.name ?? 'All branches' },
                {
                  header: 'Status',
                  cell: (s) => <Badge tone={STATUS[s.status].tone}>{STATUS[s.status].label}</Badge>,
                },
              ]}
            />
            <Pagination page={page} pageSize={25} total={staff.data.meta.total ?? 0} onPage={setPage} />
          </>
        )}
      </Card>
      <InviteStaffDialog open={inviting} onOpenChange={setInviting} />
    </PageContainer>
  );
}

function ProfileCard({ member }: { member: StaffMember }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const can = useCan();
  const branches = useQuery({
    queryKey: teamKeys.branches,
    queryFn: () => teamApi.branches({ status: 'active' }),
  });
  // Initialised from the server copy; the parent remounts this card (key) when the member changes.
  const [draft, setDraft] = useState(() => ({
    fullName: member.fullName,
    phone: member.phone ?? '',
    designation: member.designation ?? '',
    employeeCode: member.employeeCode ?? '',
    primaryBranchId: member.primaryBranch?.id ?? '',
    isAssignable: member.isAssignable,
  }));
  const save = useMutation({
    mutationFn: () =>
      teamApi.updateStaff(member.id, {
        fullName: draft.fullName,
        phone: draft.phone || null,
        designation: draft.designation || null,
        employeeCode: draft.employeeCode || null,
        primaryBranchId: draft.primaryBranchId || null,
        isAssignable: draft.isAssignable,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(teamKeys.staffMember(member.id), data);
      void queryClient.invalidateQueries({ queryKey: ['team', 'staff'] });
      toast.success('Profile saved');
    },
    onError: (err) => toast.error(err),
  });
  const editable = can('staff.edit');
  return (
    <Card>
      <CardHeader title="Profile" />
      <CardBody className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Full name"
          value={draft.fullName}
          disabled={!editable}
          onChange={(e) => setDraft({ ...draft, fullName: e.target.value })}
        />
        <TextField
          label="Phone"
          value={draft.phone}
          disabled={!editable}
          onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
        />
        <TextField
          label="Designation"
          value={draft.designation}
          disabled={!editable}
          onChange={(e) => setDraft({ ...draft, designation: e.target.value })}
        />
        <TextField
          label="Employee code"
          value={draft.employeeCode}
          disabled={!editable}
          onChange={(e) => setDraft({ ...draft, employeeCode: e.target.value })}
        />
        <SelectField
          label="Primary branch"
          placeholder="All branches"
          disabled={!editable}
          value={draft.primaryBranchId}
          onChange={(e) => setDraft({ ...draft, primaryBranchId: e.target.value })}
          options={(branches.data?.data ?? []).map((b) => ({ value: b.id, label: b.name }))}
        />
        <div className="flex items-end">
          <CheckboxField
            label="Can receive leads"
            description="Include in lead assignment"
            disabled={!editable}
            checked={draft.isAssignable}
            onChange={(e) => setDraft({ ...draft, isAssignable: e.target.checked })}
          />
        </div>
        {editable ? (
          <div className="sm:col-span-2">
            <Button onClick={() => save.mutate()} loading={save.isPending}>
              Save profile
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function RolesCard({ member }: { member: StaffMember }) {
  const toast = useToast();
  const { me } = useAuth();
  const can = useCan();
  const queryClient = useQueryClient();
  const roles = useQuery({ queryKey: teamKeys.roles, queryFn: teamApi.roles });
  const branches = useQuery({
    queryKey: teamKeys.branches,
    queryFn: () => teamApi.branches({ status: 'active' }),
  });
  const [rows, setRows] = useState<{ roleId: string; branchId: string }[]>(() =>
    member.roles.map((r) => ({ roleId: r.roleId, branchId: r.branch?.id ?? '' })),
  );
  const assignable = (roles.data ?? []).filter((r) => r.rank < (me?.rank ?? 0));
  const memberRank = Math.max(0, ...member.roles.map((r) => r.rank));
  const editable = can('roles.assign') && member.id !== me?.user.id && memberRank < (me?.rank ?? 0);
  const save = useMutation({
    mutationFn: () =>
      teamApi.setRoles(
        member.id,
        rows.map((r) => ({ roleId: r.roleId, branchId: r.branchId || null })),
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(teamKeys.staffMember(member.id), data);
      toast.success('Roles updated');
    },
    onError: (err) => toast.error(err),
  });
  return (
    <Card>
      <CardHeader
        title="Roles"
        description={
          editable
            ? 'Roles decide what this person can do, and in which branch.'
            : 'You cannot change roles for this person.'
        }
      />
      <CardBody className="space-y-3">
        {rows.map((row, i) => (
          <div key={i} className="flex flex-wrap items-end gap-2">
            <div className="min-w-44 flex-1">
              <SelectField
                label="Role"
                disabled={!editable}
                value={row.roleId}
                onChange={(e) =>
                  setRows(rows.map((r, j) => (j === i ? { ...r, roleId: e.target.value } : r)))
                }
                options={(editable ? assignable : (roles.data ?? [])).map((r) => ({
                  value: r.id,
                  label: r.name,
                }))}
              />
            </div>
            <div className="min-w-44 flex-1">
              <SelectField
                label="Branch"
                placeholder="All branches"
                disabled={!editable}
                value={row.branchId}
                onChange={(e) =>
                  setRows(rows.map((r, j) => (j === i ? { ...r, branchId: e.target.value } : r)))
                }
                options={(branches.data?.data ?? []).map((b) => ({ value: b.id, label: b.name }))}
              />
            </div>
            {editable && rows.length > 1 ? (
              <Button
                variant="ghost"
                aria-label="Remove role"
                onClick={() => setRows(rows.filter((_, j) => j !== i))}
              >
                <Trash2 aria-hidden className="size-4" />
              </Button>
            ) : null}
          </div>
        ))}
        {editable ? (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setRows([...rows, { roleId: assignable[0]?.id ?? '', branchId: '' }])}
            >
              Add role
            </Button>
            <Button size="sm" onClick={() => save.mutate()} loading={save.isPending}>
              Save roles
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/** Per-staff toggles from MP §5, mapped to permission codes (docs/security/12 §4). */
const TOGGLES: { code: string; label: string; scope: Scope }[] = [
  { code: 'leads.create', label: 'Create leads', scope: 'branch' },
  { code: 'leads.edit', label: 'Edit lead details', scope: 'assigned' },
  { code: 'leads.change_status', label: 'Change lead status', scope: 'assigned' },
  { code: 'activities.create', label: 'Add activities and notes', scope: 'assigned' },
  { code: 'followups.create', label: 'Schedule follow-ups', scope: 'assigned' },
  { code: 'leads.transfer', label: 'Transfer leads', scope: 'assigned' },
  { code: 'assignments.assign', label: 'Assign leads to others', scope: 'team' },
  { code: 'leads.bulk_update', label: 'Bulk operations', scope: 'team' },
  { code: 'leads.export', label: 'Export leads', scope: 'branch' },
  { code: 'notifications.send_manual', label: 'Manual notifications', scope: 'assigned' },
  { code: 'notification_rules.configure', label: 'Configure automated notifications', scope: 'tenant' },
  { code: 'reports.view', label: 'View reports', scope: 'team' },
];

function OverridesCard({ member }: { member: StaffMember }) {
  const toast = useToast();
  const { me } = useAuth();
  const can = useCan();
  const queryClient = useQueryClient();
  const effective = useQuery({
    queryKey: ['team', 'staff', member.id, 'effective'],
    queryFn: () => teamApi.effective(member.id),
  });
  const overrides = useQuery({
    queryKey: teamKeys.overrides(member.id),
    queryFn: () => teamApi.overrides(member.id),
  });
  const editable =
    can('roles.manage_permissions') &&
    member.id !== me?.user.id &&
    Math.max(0, ...member.roles.map((r) => r.rank)) < (me?.rank ?? 0);
  const mutate = useMutation({
    mutationFn: (next: { permission: string; effect: 'grant' | 'deny'; scope?: Scope }[]) =>
      teamApi.setOverrides(member.id, next),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['team', 'staff', member.id] });
      toast.success('Access updated');
    },
    onError: (err) => toast.error(err),
  });

  if (effective.isPending || overrides.isPending) return <TableSkeleton rows={4} />;
  if (effective.isError) return <ErrorState error={effective.error} />;
  const current = overrides.data ?? [];

  function toggle(code: string, enable: boolean, scope: Scope) {
    const rest = current
      .filter((o) => o.permission !== code)
      .map((o) => ({ permission: o.permission, effect: o.effect, ...(o.scope ? { scope: o.scope } : {}) }));
    const fromRole =
      !!effective.data &&
      !!effective.data.permissions[code] &&
      !current.some((o) => o.permission === code && o.effect === 'grant');
    // Enabling something the role already grants (or disabling a manual grant) just clears the override.
    if (enable && !current.some((o) => o.permission === code && o.effect === 'deny'))
      rest.push({ permission: code, effect: 'grant', scope });
    if (!enable && fromRole) rest.push({ permission: code, effect: 'deny' });
    mutate.mutate(rest);
  }

  return (
    <Card>
      <CardHeader
        title="Individual access"
        description="Turn single abilities on or off for this person, on top of their roles."
      />
      <ul className="divide-y divide-line">
        {TOGGLES.map((t) => {
          const on = !!effective.data.permissions[t.code];
          const override = current.find((o) => o.permission === t.code);
          return (
            <li key={t.code} className="flex items-center gap-3 px-5 py-2.5 text-sm">
              <span className="flex-1 text-fg">{t.label}</span>
              {override ? (
                <Badge tone="warning">
                  {override.effect === 'grant' ? 'Granted individually' : 'Turned off individually'}
                </Badge>
              ) : null}
              {on ? (
                <span className="text-xs text-fg-subtle">
                  {SCOPE_LABELS[effective.data.permissions[t.code]!.scope]}
                </span>
              ) : null}
              <input
                type="checkbox"
                role="switch"
                aria-label={t.label}
                className="size-4 accent-brand-600"
                checked={on}
                disabled={!editable || mutate.isPending}
                onChange={(e) => toggle(t.code, e.target.checked, t.scope)}
              />
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

export function StaffDetailPage() {
  const { id = '' } = useParams();
  const can = useCan();
  const { me } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const member = useQuery({ queryKey: teamKeys.staffMember(id), queryFn: () => teamApi.staffMember(id) });

  async function act(fn: () => Promise<unknown>, message: string) {
    try {
      await fn();
      await queryClient.invalidateQueries({ queryKey: ['team', 'staff'] });
      toast.success(message);
    } catch (err) {
      toast.error(err);
    }
  }

  if (member.isPending)
    return (
      <PageContainer width="md">
        <TableSkeleton />
      </PageContainer>
    );
  if (member.isError)
    return (
      <PageContainer width="md">
        <ErrorState error={member.error} />
      </PageContainer>
    );
  const m = member.data;
  const self = m.id === me?.user.id;

  return (
    <PageContainer width="md">
      <PageHeader
        back={{ to: '/app/team/staff', label: 'Staff' }}
        title={m.fullName}
        description={
          <span className="flex items-center gap-2">
            {m.email} <Badge tone={STATUS[m.status].tone}>{STATUS[m.status].label}</Badge>
          </span>
        }
        actions={
          can('staff.edit') && !self ? (
            <>
              {m.status === 'invited' ? (
                <Button
                  variant="secondary"
                  onClick={() => void act(() => teamApi.resendInvite(m.id), 'Invitation sent again')}
                >
                  Resend invitation
                </Button>
              ) : null}
              {m.status === 'active' ? (
                <Button
                  variant="secondary"
                  onClick={() => void act(() => teamApi.revokeSessions(m.id), 'Signed out of all devices')}
                >
                  Sign out everywhere
                </Button>
              ) : null}
              {m.status === 'disabled' ? (
                <Button
                  variant="secondary"
                  onClick={() => void act(() => teamApi.setStaffStatus(m.id, 'reactivate'), 'Reactivated')}
                >
                  Reactivate
                </Button>
              ) : (
                <ConfirmDialog
                  title={`Deactivate ${m.fullName}?`}
                  description="They are signed out immediately and cannot sign in until reactivated."
                  confirmLabel={`Deactivate ${m.fullName}`}
                  onConfirm={() => act(() => teamApi.setStaffStatus(m.id, 'deactivate'), 'Deactivated')}
                  trigger={(open) => (
                    <Button variant="danger" onClick={open}>
                      Deactivate
                    </Button>
                  )}
                />
              )}
            </>
          ) : null
        }
      />
      <div className="space-y-6">
        <ProfileCard key={`profile-${JSON.stringify(m)}`} member={m} />
        <RolesCard key={`roles-${JSON.stringify(m.roles)}`} member={m} />
        {can('roles.view') ? <OverridesCard member={m} /> : null}
      </div>
    </PageContainer>
  );
}
